import React, { useContext, useEffect, useRef, useState } from 'react';

import { ActionsContext, SettingContext } from '../../context';
import { useFetchProducts } from '../../hooks/useFetchProducts';
import { getDeviceType } from '../../utils';
import { Spinner } from '../Spinner/Spinner';
import { ProductItem } from './ProductItem';

import {
  assignedTranslateValues,
  ComponentChildSchema,
  getTranslatedText,
  withCustomizeSchema,
} from '@nizza/customize-live';
import styles from './productSlider.module.css';

import { intlFormatMessageOutsideFC } from '../../utils/intlFormatMessageOutsideFC';

const intl = intlFormatMessageOutsideFC();

type VerticalProductSliderProps = {
  height: string;
  variationSelectorState: [
    string,
    React.Dispatch<React.SetStateAction<string>>,
  ];
  transmitionType?: string | undefined;
};

export const VerticalProductSlider = ({
  height,
  variationSelectorState,
  transmitionType,
}: VerticalProductSliderProps) => {
  const {
    collectionId,
    isModalLive,
    setCollection,
    productsInBD,
    setProductsInBD,
    salesChannelId,
    setSalesChannel,
  } = useContext(SettingContext);
  const isMobile = getDeviceType() === 'mobile';

  const {
    setting: { isInfinite, time },
  } = useContext(ActionsContext);

  const { products, loading } = useFetchProducts({
    collectionId,
    setCollection,
    productsInBD,
    setProductsInBD,
    salesChannelId,
    setSalesChannel,
  });

  const [isMouseOver, setIsMouseOver] = useState(false);
  const delay = time ? time * 1000 : 10000;
  const [indexScroll, setIndexScroll] = useState(0);
  const productLisRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (loading || !products) return;
    if (!isInfinite) return;
    if (!productLisRef?.current) return;
    if (isMouseOver) return;

    const current = productLisRef?.current;

    const productDivs = current.children;

    const timeout = setTimeout(() => {
      if (indexScroll === 0) {
        current.scroll({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
      } else {
        current.scrollBy({
          top: productDivs[indexScroll - 1].clientHeight + 10,
          left: 0,
          behavior: 'smooth',
        });
      }

      setIndexScroll(
        indexScroll === productDivs.length - 1 ? 0 : indexScroll + 1,
      );
    }, delay);

    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indexScroll, isInfinite, delay, isMouseOver, products]);

  useEffect(() => {
    if (!productLisRef.current) return;
    productLisRef.current.addEventListener('mouseover', () => {
      setIsMouseOver(true);
    });
    productLisRef.current.addEventListener('mouseout', () => {
      setIsMouseOver(false);
    });
  }, [productLisRef]);

  return loading ? (
    <div className={styles.loader}>
      <Spinner />
    </div>
  ) : products ? (
    <div
      className={styles.verticalProductSliderContent}
      style={
        isModalLive && transmitionType === 'vertical' && !isMobile
          ? { maxWidth: 390, minWidth: 390 }
          : {}
      }
    >
      <div className={styles.verticalProductSliderTitle}>
        <ProductSliderHeader />
      </div>
      <div
        style={{ height: parseInt(height) }}
        className={styles.productList}
        ref={productLisRef}
        id="product-list"
      >
        {products &&
          products.length > 0 &&
          products.map((product: any) => (
            <ProductItem
              key={product.id}
              product={{ ...product, source: 'live_shopping_sidebar' }}
              variationSelectorState={variationSelectorState}
              sectionIdClickedOn="live_shopping_sidebar"
            />
          ))}
      </div>
    </div>
  ) : null;
};

const ProductSliderHeader = withCustomizeSchema(
  ({ selector, formData }: ComponentChildSchema<unknown>) => {
    return (
      <p customize-field={selector} className={styles.title}>
        {getTranslatedText(formData?.data?.sectionTitleDesktop)}
      </p>
    );
  },
  {
    products: {
      id: 'section_title_desktop',
      label: intl.formatMessage({
        id: 'customize/vertical-product-slider.section-title',
      }),
      category: 'mix',
      fields: {
        type: 'object',
        properties: {
          sectionTitleDesktop: {
            type: 'string',
            title: intl.formatMessage({
              id: 'customize/general.section-name',
            }),
            default: assignedTranslateValues({
              en: 'List of products',
              pt: 'Lista de produtos',
              es: 'Lista de productos',
              ro: 'Produse',
            }),
          },
        },
      },
      ui: {
        sectionTitleDesktop: {
          'ui:widget': 'translatableText',
        },
      },
    },
  },
);
